import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDecorLibraryItemInput,
  DecorLibraryItem,
  ReadDecorLibraryInput,
  UpdateDecorLibraryItemInput,
} from '@peles/shared';
import * as api from '../api/decorLibrary';

export const DECOR_LIBRARY_KEY = ['decor-library'] as const;

/**
 * Every piece of the library (switched-off ones too, for whoever manages
 * it). The library is the office's — a few hundred pieces at most — so it is
 * read once and filtered on the page, which keeps the picker's filters
 * instant.
 */
export function useDecorLibrary(options: { enabled?: boolean } = {}) {
  return useQuery<DecorLibraryItem[]>({
    queryKey: [...DECOR_LIBRARY_KEY, 'all'],
    queryFn: () => api.listDecorLibrary({ includeInactive: true }),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}

/** The pieces a vendor supplies. */
export function useVendorDecorPieces(vendorId: string | null | undefined) {
  return useQuery<DecorLibraryItem[]>({
    queryKey: [...DECOR_LIBRARY_KEY, 'vendor', vendorId],
    queryFn: () => api.listDecorLibrary({ vendorIds: [vendorId!] }),
    enabled: !!vendorId,
  });
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: DECOR_LIBRARY_KEY });
}

export function useCreateDecorLibraryItem() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: CreateDecorLibraryItemInput) => api.createDecorLibraryItem(input),
    onSuccess: () => void refresh(),
  });
}

export function useUpdateDecorLibraryItem() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDecorLibraryItemInput }) =>
      api.updateDecorLibraryItem(id, input),
    onSuccess: () => void refresh(),
  });
}

export function useDeleteDecorLibraryItem() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => api.deleteDecorLibraryItem(id),
    onSuccess: () => void refresh(),
  });
}

export function useReadDecorLibraryPictures() {
  return useMutation({
    mutationFn: (input: ReadDecorLibraryInput) => api.readDecorLibraryPictures(input),
  });
}
